import type { SobQuarterValues } from '../model/types'

type CalculateAnnualSobTotalParams = {
  sob: SobQuarterValues
  sales: SobQuarterValues
}

const QUARTER_KEYS = ['q1', 'q2', 'q3', 'q4'] as const

export const calculateAnnualSobTotal = ({
  sob,
  sales,
}: CalculateAnnualSobTotalParams): number | null => {
  const quarters = QUARTER_KEYS.map((quarter) => ({
    sob: sob[quarter],
    sales: sales[quarter],
  }))

  // Method A: Weighted Average
  // Use only quarters where both SOB and Sales are > 0
  const weightedQuarters = quarters.filter(
    (item): item is { sob: number; sales: number } =>
      typeof item.sob === 'number' &&
      Number.isFinite(item.sob) &&
      item.sob > 0 &&
      typeof item.sales === 'number' &&
      Number.isFinite(item.sales) &&
      item.sales > 0
  )

  if (weightedQuarters.length > 0) {
    const totalSales = weightedQuarters.reduce((sum, item) => sum + item.sales, 0)

    const totalBusiness = weightedQuarters.reduce(
      (sum, item) => sum + item.sales / (item.sob / 100),
      0
    )

    return totalBusiness > 0 ? (totalSales / totalBusiness) * 100 : null
  }

  // Method B: Simple Average fallback
  // Use only quarters where SOB > 0
  const fallbackQuarters = quarters.filter(
    (item): item is { sob: number; sales: number | null } =>
      typeof item.sob === 'number' && Number.isFinite(item.sob) && item.sob > 0
  )

  if (fallbackQuarters.length === 0) {
    return null
  }

  const totalSob = fallbackQuarters.reduce((sum, item) => sum + item.sob, 0)
  return totalSob / fallbackQuarters.length
}
